import {
  CmsComponentData,
  CmsComponentGrid,
  ContentComponentLibraryProvider,
} from '@johnlewispartnership/wtr-content-component-library';
import type { Config } from '@johnlewispartnership/wtr-content-component-library/dist/component-library/config/types';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { NuqsAdapter } from 'nuqs/adapters/react';

import { logger } from 'logger';
import { ContentPageData } from 'services/content/types';
import { PreloadedData } from 'server/types';
import cmsComponentMap from './componentMap';
import AnalyticsSetup from './components/AnalyticsSetup';
import PageLayout from './pages/PageLayout';

// For now, we always import the global grid styles
import './styles/grid.scss';
import { useHeadCollector } from './context/headCollectorContext';

export type PageAppProps = {
  componentData: CmsComponentData[];
  /**
   * componentLibraryConfig: for environment based config options that need to come from server
   */
  componentLibraryConfig: Omit<Config, 'componentMap'>;
  pageData: ContentPageData;
  preloadedData?: PreloadedData;
};

const PageAppContent = ({
  componentData,
  componentLibraryConfig,
}: Pick<PageAppProps, 'componentData' | 'componentLibraryConfig'>) => {
  const { trackEvent } = useAnalytics();

  const headCollector = useHeadCollector();

  /**
   * For hardcoded isomorphic config options
   */
  const config: Config = {
    componentMap: cmsComponentMap,
    imageQuality: 'highQuality85',
    onAnalyticsEvent: trackEvent,
    onError: logger.error,
    headFunction: metadata => {
      headCollector?.add(metadata);
      return <></>;
    },
    ...componentLibraryConfig,
  };

  return (
    <ContentComponentLibraryProvider config={config}>
      <CmsComponentGrid childComponents={componentData} position="root" preloadImages />
    </ContentComponentLibraryProvider>
  );
};

// NOTE: This is used on both the server & client.
const PageApp = ({
  componentData,
  componentLibraryConfig,
  pageData,
  preloadedData,
}: PageAppProps) => (
  <NuqsAdapter>
    <AnalyticsSetup>
      <PageLayout pageData={pageData} preloadedData={preloadedData}>
        <PageAppContent
          componentData={componentData}
          componentLibraryConfig={componentLibraryConfig}
        />
      </PageLayout>
    </AnalyticsSetup>
  </NuqsAdapter>
);

export default PageApp;
